import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../../api/api';
import { MaterialRequirementsPlan } from '../../types';
import ProcurementPlanningTabs from '../../components/ProcurementPlanningTabs';
import './OrderSupplyPage.css';

interface PurchasePlanItem {
  productId: number;
  productName: string;
  categoryName: string | null;
  weightType: string;
  currentBalance: number;
  totalConsumedInPeriod: number;
  avgDailyUsage: number;
  daysRemaining: number | null;
  recommendedPurchaseQty: number;
  priority: 'critical' | 'warning' | 'ok';
  recommendedSupplierId: number | null;
  recommendedSupplierName: string | null;
  recommendedUnitPrice: number | null;
  riskAdjustedUnitPrice: number | null;
  supplierProcurementScore: number | null;
  supplierRiskLevel: 'normal' | 'warning' | 'critical' | null;
  estimatedProcurementCost: number | null;
  riskAdjustedEstimatedCost: number | null;
  supplierAlternatives: Array<{
    supplierId: number;
    supplierName: string;
    unitPrice: number;
    riskAdjustedUnitPrice: number;
    procurementScore: number;
    riskLevel: 'normal' | 'warning' | 'critical';
  }>;
}

interface PurchasePlanResponse {
  analysisDays: number;
  forecastDays: number;
  generatedAt: string;
  items: PurchasePlanItem[];
}

const PRIORITY_COLOR: Record<string, string> = {
  critical: 'var(--crm-danger)',
  warning: 'var(--crm-warning)',
  ok: 'var(--crm-success)',
};
const PRIORITY_BACKGROUND: Record<string, string> = {
  critical: 'var(--crm-danger-bg)',
  warning: 'var(--crm-warning-bg)',
  ok: 'var(--crm-success-bg)',
};
const PRIORITY_LABEL: Record<string, string> = {
  critical: '🔴 Критично',
  warning: '🟡 Увага',
  ok: '🟢 В нормі',
};

const thStyle: React.CSSProperties = { padding: '10px', textAlign: 'left', background: 'var(--crm-primary)', color: 'var(--crm-primary-contrast)', borderBottom: '2px solid var(--crm-border)', whiteSpace: 'nowrap' };
const tdStyle: React.CSSProperties = { padding: '8px 10px', borderBottom: '1px solid var(--crm-table-row-border)', color: 'var(--crm-table-text)' };
const fieldStyle: React.CSSProperties = { border: '1px solid var(--crm-border)', borderRadius: 'var(--crm-radius-sm)', background: 'var(--crm-input-bg)', color: 'var(--crm-text)', padding: '7px 12px' };
const actionButtonStyle: React.CSSProperties = { padding: '8px 16px', background: 'var(--crm-info-bg)', color: 'var(--crm-info)', border: '1px solid var(--crm-info)', borderRadius: 'var(--crm-radius-sm)', cursor: 'pointer' };

const PurchasePlanningPage: React.FC = () => {
  const [data, setData] = useState<PurchasePlanResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [analysisDays, setAnalysisDays] = useState(30);
  const [forecastDays, setForecastDays] = useState(14);
  const [priorityFilter, setPriorityFilter] = useState<'all' | 'critical' | 'warning'>('all');
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [mrp, setMrp] = useState<MaterialRequirementsPlan | null>(null);
  const [mrpFrom, setMrpFrom] = useState(new Date().toISOString().slice(0, 10));
  const [mrpTo, setMrpTo] = useState(new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10));
  const [leadTimeDays, setLeadTimeDays] = useState(0);
  const [safetyStockDays, setSafetyStockDays] = useState(0);
  const [orderMultiple, setOrderMultiple] = useState(1);
  const [mrpLoading, setMrpLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get<PurchasePlanResponse>('/purchaseplanning', {
        params: { analysisDays, forecastDays },
      });
      setData(res.data);
    } catch (e: any) {
      setError('Не вдалося завантажити план закупівель.');
    } finally {
      setLoading(false);
    }
  }, [analysisDays, forecastDays]);

  useEffect(() => { load(); }, [load]);

  const loadMrp = async () => {
    setMrpLoading(true);
    setError(null);
    try {
      const res = await api.get<MaterialRequirementsPlan>('/purchaseplanning/mrp', {
        params: { from: mrpFrom, to: mrpTo, leadTimeDays, safetyStockDays, orderMultiple },
      });
      setMrp(res.data);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Не вдалося завантажити MRP-рекомендацію.');
    } finally {
      setMrpLoading(false);
    }
  };

  const categories = useMemo(
    () => Array.from(new Set((data?.items ?? []).map(i => i.categoryName || 'Без категорії'))).sort(),
    [data]
  );

  const filtered = useMemo(() => {
    if (!data) return [];
    return data.items.filter(item => {
      if (priorityFilter !== 'all' && item.priority !== priorityFilter) return false;
      if (categoryFilter && (item.categoryName || 'Без категорії') !== categoryFilter) return false;
      if (search && !item.productName.toLowerCase().includes(search.toLowerCase())) return false;
      return true;
    });
  }, [data, priorityFilter, categoryFilter, search]);

  const summary = useMemo(() => {
    if (!data) return null;
    const critical = data.items.filter(i => i.priority === 'critical').length;
    const warning = data.items.filter(i => i.priority === 'warning').length;
    const needBuy = data.items.filter(i => i.recommendedPurchaseQty > 0).length;
    return { critical, warning, needBuy };
  }, [data]);

  const cardStyle = (color: string): React.CSSProperties => ({
    background: 'var(--crm-surface)', color: 'var(--crm-text)', border: '1px solid var(--crm-border)',
    borderRadius: 'var(--crm-radius-md)', padding: '14px 18px',
    boxShadow: 'var(--crm-shadow-sm)', borderLeft: `4px solid ${color}`,
  });

  return (
    <div className="page-shell" style={{ padding: '24px', maxWidth: 1200 }}>
      <ProcurementPlanningTabs />
      <h2 style={{ marginTop: 0 }}>📋 Планування закупівель</h2>
      <p style={{ color: 'var(--crm-text-muted)', marginBottom: 20 }}>
        Аналіз середнього споживання за останні <b>{analysisDays}</b> днів та рекомендації на наступні <b>{forecastDays}</b> днів.
      </p>

      {/* Controls */}
      <div className="purchase-planning-period-controls" style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 20, alignItems: 'flex-end' }}>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13, color: 'var(--crm-text)' }}>
          Аналіз (днів)
          <input type="number" min={7} max={365} value={analysisDays}
            onChange={e => setAnalysisDays(Number(e.target.value))}
            style={{ ...fieldStyle, padding: '6px 10px', width: 90 }} />
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13, color: 'var(--crm-text)' }}>
          Прогноз (днів)
          <input type="number" min={1} max={90} value={forecastDays}
            onChange={e => setForecastDays(Number(e.target.value))}
            style={{ ...fieldStyle, padding: '6px 10px', width: 90 }} />
        </label>
        <button onClick={load} disabled={loading}
          style={{ ...actionButtonStyle, padding: '8px 20px', alignSelf: 'flex-end' }}>
          {loading ? 'Завантаження…' : '🔄 Оновити'}
        </button>
      </div>

      {error && <div role="alert" style={{ background: 'var(--crm-danger-bg)', color: 'var(--crm-danger)', border: '1px solid var(--crm-danger)', padding: 12, borderRadius: 'var(--crm-radius-sm)', marginBottom: 16 }}>{error}</div>}

      {/* Summary cards */}
      {summary && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, marginBottom: 24 }}>
          <div style={cardStyle('var(--crm-danger)')}>
            <div style={{ fontSize: 11, color: 'var(--crm-text-muted)', marginBottom: 4 }}>Критичних позицій</div>
            <div style={{ fontSize: 28, fontWeight: 700, color: 'var(--crm-danger)' }}>{summary.critical}</div>
          </div>
          <div style={cardStyle('var(--crm-warning)')}>
            <div style={{ fontSize: 11, color: 'var(--crm-text-muted)', marginBottom: 4 }}>Потребують уваги</div>
            <div style={{ fontSize: 28, fontWeight: 700, color: 'var(--crm-warning)' }}>{summary.warning}</div>
          </div>
          <div style={cardStyle('var(--crm-info)')}>
            <div style={{ fontSize: 11, color: 'var(--crm-text-muted)', marginBottom: 4 }}>Рекомендовано закупити</div>
            <div style={{ fontSize: 28, fontWeight: 700, color: 'var(--crm-info)' }}>{summary.needBuy}</div>
          </div>
          <div style={cardStyle('var(--crm-border)')}>
            <div style={{ fontSize: 11, color: 'var(--crm-text-muted)', marginBottom: 4 }}>Всього позицій</div>
            <div style={{ fontSize: 28, fontWeight: 700, color: 'var(--crm-text)' }}>{data?.items.length ?? 0}</div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="purchase-planning-filter-controls" style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 16, alignItems: 'center' }}>
        <input placeholder="🔍 Пошук товару…" value={search} onChange={e => setSearch(e.target.value)}
          style={{ ...fieldStyle, minWidth: 200 }} />
        <select value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)}
          style={fieldStyle}>
          <option value="">Всі категорії</option>
          {categories.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <select value={priorityFilter} onChange={e => setPriorityFilter(e.target.value as any)}
          style={fieldStyle}>
          <option value="all">Всі пріоритети</option>
          <option value="critical">🔴 Критично</option>
          <option value="warning">🟡 Увага</option>
        </select>
      </div>

      {/* Table */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 40, color: 'var(--crm-text-muted)' }}>Завантаження…</div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table className="crm-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr>
                <th style={thStyle}>Статус</th>
                <th style={thStyle}>Товар</th>
                <th style={thStyle}>Категорія</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Залишок</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Сер. витрата/день</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Залишилось днів</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Рекомендовано закупити</th>
                <th style={thStyle}>Рекомендований постачальник</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={8} style={{ ...tdStyle, textAlign: 'center', color: 'var(--crm-text-muted)', padding: 30 }}>Позицій не знайдено</td></tr>
              ) : filtered.map(item => (
                <tr key={item.productId}>
                  <td style={tdStyle}>
                    <span style={{ display: 'inline-block', padding: '3px 6px', borderRadius: 'var(--crm-radius-sm)', background: PRIORITY_BACKGROUND[item.priority], color: PRIORITY_COLOR[item.priority], fontWeight: 600, fontSize: 12 }}>
                      {PRIORITY_LABEL[item.priority]}
                    </span>
                  </td>
                  <td style={{ ...tdStyle, fontWeight: 500 }}>{item.productName}</td>
                  <td style={{ ...tdStyle, color: 'var(--crm-text-muted)' }}>{item.categoryName || '—'}</td>
                  <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 600, color: item.currentBalance < 0 ? 'var(--crm-danger)' : 'var(--crm-text)' }}>
                    {item.currentBalance.toFixed(3)} {item.weightType}
                  </td>
                  <td style={{ ...tdStyle, textAlign: 'right', color: 'var(--crm-text-muted)' }}>
                    {item.avgDailyUsage > 0 ? `${item.avgDailyUsage.toFixed(3)} ${item.weightType}` : '—'}
                  </td>
                  <td style={{ ...tdStyle, textAlign: 'right', color: item.daysRemaining !== null && item.daysRemaining <= 3 ? 'var(--crm-danger)' : item.daysRemaining !== null && item.daysRemaining <= 7 ? 'var(--crm-warning)' : 'var(--crm-success)' }}>
                    {item.daysRemaining !== null ? `${item.daysRemaining} д.` : '—'}
                  </td>
                  <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 600, color: item.recommendedPurchaseQty > 0 ? 'var(--crm-info)' : 'var(--crm-text-muted)' }}>
                    {item.recommendedPurchaseQty > 0 ? `${item.recommendedPurchaseQty} ${item.weightType}` : '—'}
                  </td>
                  <td style={tdStyle}>
                    {item.recommendedSupplierName ? (
                      <>
                        <div style={{ fontWeight: 600 }}>{item.recommendedSupplierName}</div>
                        <div style={{ fontSize: 11, color: item.supplierRiskLevel === 'critical' ? 'var(--crm-danger)' : item.supplierRiskLevel === 'warning' ? 'var(--crm-warning)' : 'var(--crm-success)' }}>
                          рейтинг {item.supplierProcurementScore?.toFixed(1)} · {item.supplierRiskLevel === 'critical' ? 'критичний ризик' : item.supplierRiskLevel === 'warning' ? 'увага' : 'низький ризик'}
                        </div>
                        <div style={{ fontSize: 11, color: 'var(--crm-text-muted)' }}>
                          {item.recommendedUnitPrice?.toFixed(2)} грн · з ризиком {item.riskAdjustedUnitPrice?.toFixed(2)}
                        </div>
                        {item.recommendedPurchaseQty > 0 && (
                          <div style={{ fontSize: 11, color: 'var(--crm-text)', marginTop: 2 }}>
                            Очікувано: <strong>{item.estimatedProcurementCost?.toFixed(2)} грн</strong>
                            {item.riskAdjustedEstimatedCost !== item.estimatedProcurementCost
                              ? ` · з ризиком ${item.riskAdjustedEstimatedCost?.toFixed(2)}`
                              : ''}
                          </div>
                        )}
                        {item.supplierAlternatives.length > 1 && (
                          <div title={item.supplierAlternatives.slice(1).map(offer =>
                            `${offer.supplierName}: ${offer.unitPrice.toFixed(2)} грн, рейтинг ${offer.procurementScore.toFixed(1)}`
                          ).join('\n')} style={{ fontSize: 10, color: 'var(--crm-text-muted)', marginTop: 2 }}>
                            Ще варіантів: {item.supplierAlternatives.length - 1}
                          </div>
                        )}
                      </>
                    ) : <span style={{ color: 'var(--crm-text-muted)' }}>Немає пропозицій</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {data && (
        <p style={{ color: 'var(--crm-text-muted)', fontSize: 11, marginTop: 12 }}>
          Сформовано: {new Date(data.generatedAt).toLocaleString('uk-UA')}
        </p>
      )}

      <section style={{ marginTop: 32, padding: 18, background: 'var(--crm-surface)', color: 'var(--crm-text)', border: '1px solid var(--crm-border)', borderRadius: 'var(--crm-radius-lg)' }}>
        <h3 style={{ marginTop: 0, color: 'var(--crm-text)' }}>MRP підтверджених подій</h3>
        <p style={{ color: 'var(--crm-text-muted)', fontSize: 13 }}>
          Read-only розрахунок. Документи закупівлі не створюються автоматично.
        </p>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'flex-end' }}>
          <label>З <input type="date" value={mrpFrom} onChange={e => setMrpFrom(e.target.value)} /></label>
          <label>По <input type="date" value={mrpTo} onChange={e => setMrpTo(e.target.value)} /></label>
          <label>Lead time, днів <input type="number" min={0} max={90} value={leadTimeDays} onChange={e => setLeadTimeDays(Number(e.target.value))} /></label>
          <label>Safety stock, днів <input type="number" min={0} max={90} value={safetyStockDays} onChange={e => setSafetyStockDays(Number(e.target.value))} /></label>
          <label>Кратність <input type="number" min={0.001} step="0.001" value={orderMultiple} onChange={e => setOrderMultiple(Number(e.target.value))} /></label>
          <button type="button" onClick={loadMrp} disabled={mrpLoading} style={actionButtonStyle}>
            {mrpLoading ? 'Розрахунок…' : 'Розрахувати MRP'}
          </button>
        </div>
        {mrp && (
          <>
            <div style={{ marginTop: 14, fontSize: 13, color: 'var(--crm-text-muted)' }}>
              Підтверджених подій: <strong>{mrp.confirmedEventCount}</strong> ·
              позицій до закупівлі: <strong>{mrp.items.length}</strong> ·
              {` ${mrp.planningBasis}`}
            </div>
            <div style={{ overflowX: 'auto', marginTop: 12 }}>
              <table className="crm-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead><tr>
                  {['Товар', 'Попит подій', 'Залишок', 'Резерв', 'Буфер', 'Рекомендація', 'Постачальник', 'Орієнтовно'].map(header => <th key={header} style={thStyle}>{header}</th>)}
                </tr></thead>
                <tbody>{mrp.items.map(item => <tr key={item.productId}>
                  <td style={tdStyle}><strong>{item.productName}</strong><div style={{ color: 'var(--crm-text-muted)' }}>{item.categoryName || 'Без категорії'}</div></td>
                  <td style={tdStyle}>{item.confirmedEventDemand} {item.weightType}</td>
                  <td style={tdStyle}>{item.availableStock} {item.weightType}</td>
                  <td style={tdStyle}>{item.reservedInWorksheets} {item.weightType}</td>
                  <td style={tdStyle}>{item.planningBuffer} {item.weightType}</td>
                  <td style={{ ...tdStyle, fontWeight: 700, color: 'var(--crm-info)' }}>{item.recommendedPurchaseQuantity} {item.weightType}</td>
                  <td style={tdStyle}>{item.recommendedSupplierName || 'Не визначено'}</td>
                  <td style={tdStyle}>{item.estimatedCost == null ? '—' : `${item.estimatedCost.toFixed(2)} грн`}</td>
                </tr>)}</tbody>
              </table>
            </div>
          </>
        )}
      </section>
    </div>
  );
};

export default PurchasePlanningPage;
